import { Icon, type IconDefinition } from '@vrplatform/icons';
import { EllipsisMenu } from '@vrplatform/components-legacy';
import type { ThemeColor } from '@vrplatform/theme';
import {
  Box,
  Center,
  Group,
  type LoaderProps,
  Button as MButton,
  type MantineSize,
  Tooltip,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import type { EmotionSx as Sx } from '@mantine/emotion';
import type React from 'react';
import {
  type ComponentPropsWithoutRef,
  type JSX,
  type MouseEventHandler,
  type PropsWithChildren,
  forwardRef,
} from 'react';
import type { Link } from 'react-router';

export type ButtonVariant =
  | 'transparent'
  | 'light'
  | 'primary'
  | 'default'
  | 'filled';

type BaseProps = {
  children: React.ReactNode;
  variant?: ButtonVariant;
  iconColor?: string;
  leftIconSize?: number;
  rightIconSize?: number;
  leftIcon?:
    | IconDefinition
    | ((opts: { color?: string; size?: number }) => JSX.Element);
  rightIcon?:
    | IconDefinition
    | ((opts: { color?: string; size?: number }) => JSX.Element);
  onClick?: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  color?: ThemeColor | 'primary';
  size?: MantineSize;
  sx?: Sx;
  menu?: MenuBaseProps & {
    items: React.ReactNode;
  };
  loaderProps?: LoaderProps;
  /** Disables the button and shows the reason in a tooltip on hover and keyboard focus. */
  disabledReason?: string;
} & Omit<ComponentPropsWithoutRef<'button'>, 'color'>;

type Props = BaseProps &
  (
    | { component?: 'button' }
    | { component?: 'a'; href: string; target?: string; rel?: string }
    | { component?: typeof Link; to: string }
  );

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      variant,
      leftIcon: LIcon,
      rightIcon: RIcon,
      color,
      component,
      menu,
      leftIconSize = 18,
      rightIconSize = 18,
      iconColor: ic,
      disabledReason,
      ...props
    },
    ref
  ) => {
    'use memo';

    const { colorScheme } = useMantineColorScheme();
    const { black, colors, primaryColor, radius, white } = useMantineTheme();

    const iconColor =
      ic ||
      (color
        ? colors[color === 'primary' ? primaryColor : color][6]
        : variant === 'primary'
          ? '#fff'
          : colors.neutral[5]);

    const disabled = !!props.disabled || !!disabledReason;
    // A natively disabled button gets no pointer or focus events, so its tooltip
    // never opens. Keep it focusable and block the click instead.
    const disabledReasonProps = disabledReason
      ? {
          disabled: false,
          'data-disabled': true,
          'aria-disabled': true,
          onClick: (event: React.MouseEvent<HTMLButtonElement>) =>
            event.preventDefault(),
        }
      : undefined;

    const hasMenu = !!menu;
    const isMenuSeparate = !!props.onClick;
    const menuLabel =
      typeof children === 'string' ? `${children} menu` : 'Menu';

    let buttonComponent: React.ReactNode = null;

    const paddingRight = hasMenu ? (isMenuSeparate ? 10 : 5) : undefined;
    const borderRadius =
      hasMenu && isMenuSeparate ? `${radius.md} 0 0 ${radius.md}` : radius.md;

    const menuComponent = (
      <Menu
        iconColor={iconColor}
        variant={variant || 'default'}
        isSeparated={isMenuSeparate}
        disabled={disabled}
        loading={!!props.loading}
        label={menuLabel}
        {...menu}
      >
        {menu?.items}
      </Menu>
    );

    const menuIndicator = hasMenu ? (
      <MenuIndicator
        colors={colors}
        disabled={disabled}
        iconColor={iconColor}
        isSeparated={isMenuSeparate}
        loading={!!props.loading}
        primaryColor={primaryColor}
        radius={radius.md}
        variant={variant || 'default'}
        white={white}
      />
    ) : null;

    const content = (
      <>
        <span>{children}</span>
        {!isMenuSeparate && hasMenu && menuIndicator}
      </>
    );

    const LeftIcon =
      typeof LIcon === 'string' ? (
        <Icon icon={LIcon} color={iconColor} size={leftIconSize} />
      ) : LIcon ? (
        <LIcon color={iconColor} size={leftIconSize} />
      ) : undefined;
    const RightIcon =
      typeof RIcon === 'string' ? (
        <Icon icon={RIcon} color={iconColor} size={rightIconSize} />
      ) : RIcon ? (
        <RIcon color={iconColor} size={rightIconSize} />
      ) : undefined;

    if (variant === 'transparent' || variant === 'light') {
      const textColor = color
        ? colors[color === 'primary' ? primaryColor : color][6]
        : colorScheme === 'dark'
          ? white
          : black;

      const variantColor = color || 'gray';
      buttonComponent = (
        <MButton
          ref={ref}
          component={component as any}
          variant={variant === 'light' ? 'subtle' : 'white'}
          color={variantColor}
          leftSection={LeftIcon}
          rightSection={RightIcon}
          styles={(_theme) => ({
            label: {
              fontWeight: 400,
              color: textColor,
            },
            root: {
              backgroundColor:
                variant === 'transparent' ? 'transparent' : undefined,
              '&:disabled, &[data-disabled]': {
                backgroundColor: 'initial',
                opacity: 0.5,
              },
              ':hover':
                variant === 'transparent'
                  ? {
                      backgroundColor: 'transparent',
                    }
                  : undefined,
            },
          })}
          pr={paddingRight}
          {...props}
          {...disabledReasonProps}
        >
          {content}
        </MButton>
      );
    } else if (variant === 'filled') {
      const variantColor = color || 'gray';
      buttonComponent = (
        <MButton
          ref={ref}
          component={component as any}
          variant={'filled'}
          color={variantColor}
          leftSection={LeftIcon}
          rightSection={RightIcon}
          pr={paddingRight}
          {...props}
          {...disabledReasonProps}
        >
          {content}
        </MButton>
      );
    } else if (variant === 'primary') {
      buttonComponent = (
        <MButton
          ref={ref}
          variant="filled"
          color={primaryColor}
          component={component as any}
          leftSection={LeftIcon}
          rightSection={RightIcon}
          styles={(theme) => ({
            label: {
              fontWeight: 500,
              color: 'white',
            },
            root: {
              background:
                hasMenu && isMenuSeparate
                  ? theme.colors[theme.primaryColor][6]
                  : `linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.00) 100%), ${
                      theme.colors[theme.primaryColor][6]
                    }`,
              border:
                hasMenu && isMenuSeparate
                  ? undefined
                  : `1px solid ${
                      theme.colors[theme.primaryColor][
                        colorScheme === 'dark' ? 9 : 6
                      ]
                    }`,
              borderRadius,
              '::before': {
                borderRadius: `${borderRadius}!important`,
              },
              '&:disabled, &[data-disabled]': {
                opacity: 0.5,
              },
              '&:hover':
                colorScheme === 'dark' || theme.primaryColor === 'vrplatform'
                  ? {
                      backgroundColor: theme.colors[theme.primaryColor][7],
                      borderColor: 'transparent',
                    }
                  : {
                      backgroundColor: theme.colors[theme.primaryColor][8],
                    },
            },
          })}
          pr={paddingRight}
          {...props}
          {...disabledReasonProps}
        >
          {content}
        </MButton>
      );
    } else {
      buttonComponent = (
        <MButton
          ref={ref}
          variant="default"
          component={component as any}
          leftSection={LeftIcon}
          rightSection={RightIcon}
          styles={() => ({
            label: {
              fontWeight: 500,
            },
            root: {
              background: hasMenu
                ? undefined
                : colorScheme === 'dark'
                  ? undefined
                  : 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(193, 199, 208, 0.02) 100%), #FFF',
              boxShadow: hasMenu
                ? undefined
                : '0px 0px 0px 1px rgba(0, 0, 0, 0.02)',
              borderRadius,
              '&:disabled, &[data-disabled]': {
                borderColor: colors.neutral[2],
              },
            },
          })}
          pr={paddingRight}
          {...props}
          {...disabledReasonProps}
        >
          {content}
        </MButton>
      );
    }

    // Always render the Tooltip so a changing reason does not remount the button.
    // `disabled` only hides the popup; the events keep it from tracking hover and
    // focus on buttons without a reason.
    buttonComponent = (
      <Tooltip
        disabled={!disabledReason}
        events={{
          hover: !!disabledReason,
          focus: !!disabledReason,
          touch: !!disabledReason,
        }}
        label={disabledReason}
        maw={400}
        multiline
        withArrow
      >
        {buttonComponent}
      </Tooltip>
    );

    if (!isMenuSeparate && hasMenu)
      return (
        <Menu
          target={buttonComponent}
          iconColor={iconColor}
          variant={variant || 'default'}
          isSeparated={isMenuSeparate}
          disabled={disabled}
          loading={!!props.loading}
          {...menu}
        >
          {menu?.items}
        </Menu>
      );

    if (isMenuSeparate && hasMenu) {
      return (
        <Group wrap="nowrap" gap={0} sx={{ alignItems: 'stretch' }}>
          {buttonComponent}
          {isMenuSeparate && hasMenu && menuComponent}
        </Group>
      );
    }

    return buttonComponent;
  }
);

type MenuBaseProps = {
  trigger?: 'click' | 'hover';
  width?: number | 'target';
  zIndex?: number;
};

type MenuProps = MenuBaseProps & {
  iconColor: string;
  target?: React.ReactNode;
  variant: ButtonVariant;
  isSeparated: boolean;
  disabled: boolean;
  loading: boolean;
  label?: string;
};

const Menu = ({
  trigger = 'click',
  children,
  iconColor,
  target,
  width,
  zIndex,
  variant,
  isSeparated,
  disabled,
  loading,
  label,
}: PropsWithChildren<MenuProps>) => {
  const { colors, primaryColor, radius, ...theme } = useMantineTheme();

  return (
    <EllipsisMenu
      width={width}
      trigger={trigger}
      zIndex={zIndex}
      disabled={disabled || loading}
      withinPortal
      withArrow
      target={
        target ? (
          <Box>{target}</Box>
        ) : (
          <MenuIndicator
            colors={colors}
            disabled={disabled}
            iconColor={iconColor}
            isSeparated={isSeparated}
            loading={loading}
            primaryColor={primaryColor}
            radius={radius.md}
            variant={variant}
            white={theme.white}
            label={label}
          />
        )
      }
    >
      {children}
    </EllipsisMenu>
  );
};

type MenuIndicatorProps = {
  colors: ReturnType<typeof useMantineTheme>['colors'];
  disabled: boolean;
  iconColor: string;
  isSeparated: boolean;
  loading: boolean;
  primaryColor: string;
  radius: string | number;
  variant: ButtonVariant;
  white: string;
  label?: string;
} & ComponentPropsWithoutRef<'div'>;

const MenuIndicator = forwardRef<HTMLDivElement, MenuIndicatorProps>(
  (
    {
      colors,
      disabled,
      iconColor,
      isSeparated,
      loading,
      primaryColor,
      radius,
      variant,
      white,
      label,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (
      event
    ) => {
      onKeyDown?.(event);

      if (
        event.defaultPrevented ||
        disabled ||
        loading ||
        (event.key !== 'Enter' && event.key !== ' ')
      )
        return;

      event.preventDefault();
      event.currentTarget.click();
    };

    if (!isSeparated) {
      return (
        <Center
          ref={ref}
          ml={6}
          sx={{
            flexShrink: 0,
            opacity: disabled || loading ? 0.5 : undefined,
          }}
          onKeyDown={onKeyDown}
          {...props}
        >
          <Icon icon="ChevronIcon" size={20} color={iconColor} />
        </Center>
      );
    }

    return (
      <Center
        ref={ref}
        role="button"
        tabIndex={disabled || loading ? undefined : 0}
        aria-disabled={disabled || loading ? true : undefined}
        aria-label={label}
        onKeyDown={handleKeyDown}
        px={isSeparated ? 5 : undefined}
        pl={isSeparated ? undefined : 5}
        ml={isSeparated ? undefined : 10}
        sx={{
          ...(variant === 'primary' && {
            borderLeft: '1px solid',
            borderLeftColor: colors.neutral[3],
          }),
          ...(variant === 'default' && {
            borderRight: '1px solid',
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: colors.neutral[3],
          }),
          flex: 1,
          height: isSeparated ? undefined : '100%',
          borderRadius: `0 ${radius} ${radius} 0`,
          opacity: disabled || loading ? 0.5 : undefined,
          backgroundColor: disabled
            ? '#adb5bd'
            : !isSeparated
              ? undefined
              : variant === 'primary'
                ? colors[primaryColor][6]
                : white,
          cursor: disabled || loading ? undefined : 'pointer',
          ':hover':
            isSeparated && !(disabled || loading)
              ? {
                  backgroundColor:
                    variant === 'primary'
                      ? colors[primaryColor][7]
                      : variant === 'default'
                        ? colors.gray[0]
                        : undefined,
                }
              : undefined,
        }}
        {...props}
      >
        <Icon icon="ChevronIcon" size={20} color={iconColor} />
      </Center>
    );
  }
);

MenuIndicator.displayName = 'MenuIndicator';
